import React, { useEffect, useRef } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useSelector, useDispatch } from 'react-redux';
import { addCommas } from 'core/utils/helpers/numbers';
import { encodeQueryString, redirectToTheTargetPage } from 'core/utils/helpers/url';
import { selectSiteUrl } from 'core/selectors/app';
import { selectUserIsIdentified } from 'core/selectors/user';

import { ShowOnMQ } from 'core/modules/MediaQueries/MediaQueries';
import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import SectionTitle from 'core/modules/SectionTitle/SectionTitle';
import ProductItem from 'core/modules/ProductItem/ProductItem';
import Sticky from 'core/modules/Sticky/Sticky';
import StickyContent from 'core/modules/Sticky/StickyContent';
import SlideMenu from 'core/modules/Partials/SlideMenu/SlideMenu';

import { useMultipleFilters } from 'core/hooks/useMultipleFilters';

import defineFilters from './utils/defineFilters';

import MenuMobile from './components/MenuMobile/MenuMobile';
import MenuDesktop from './components/MenuDesktop/MenuDesktop';

import { useGetProductSearchResults, useSortActiveFiltersByAdding } from './hooks';

import {
  selectProductSearchResultsIsLoaded, selectProductSearchResultMetadata,
  selectProductSearchResultResponse, selectProductSearchResultInitialFacets,
  selectProductSearchResultsIsFiltering, selectProductSearchResultsIsFiltered,
  selectProductSearchResultsConfig,
} from './selectors';
import { triggerPsrIsViewed } from './actions';

import './ProductSearchResults.scss';

function ProductSearchResults() {
  const dispatch = useDispatch();
  const sectionRef = useRef();

  const siteUrl = useSelector(selectSiteUrl);
  const isLoaded = useSelector(selectProductSearchResultsIsLoaded);
  const isUserIdentified = useSelector(selectUserIsIdentified);
  const isFiltering = useSelector(selectProductSearchResultsIsFiltering);
  const isFiltered = useSelector(selectProductSearchResultsIsFiltered);
  const products = useSelector(selectProductSearchResultResponse);
  const initialFacets = useSelector(selectProductSearchResultInitialFacets);
  const { total } = useSelector(selectProductSearchResultMetadata);
  const config = useSelector(selectProductSearchResultsConfig);

  const filterHook = useMultipleFilters();
  const { filters, setFilters, hasIdsInAnyFilters } = filterHook;
  const productResultsHook = useGetProductSearchResults(filters);
  const { sortedActiveFilters } = useSortActiveFiltersByAdding(filters);
  const { searchParams, fetchMoreProducts } = productResultsHook;
  const { rebateOptions, extraLabel } = config;

  const shouldShowExpandButton = total > products.length;
  const hasProducts = !isEmpty(products);

  const redirectToNoResultsPage = () => {
    redirectToTheTargetPage(siteUrl, 'nr____.htm', encodeQueryString({ keywords: searchParams.q }));
  };

  // Define initial filter state
  useEffect(() => {
    if (isLoaded) {
      const initialFilterState = defineFilters(initialFacets);
      setFilters(initialFilterState);
    }
  }, [isLoaded]);

  useEffect(() => {
    dispatch(triggerPsrIsViewed(searchParams.q));
  }, []);

  useEffect(() => {
    if (isLoaded && !hasProducts) {
      redirectToNoResultsPage();
    }
  }, [isLoaded]);

  if (!isLoaded) {
    return null;
  }

  if (!hasProducts && !hasIdsInAnyFilters && !isFiltering && !isFiltered) {
    return null;
  }

  const sectionTitle =
    <>We found {addCommas(total)} products <span className="mn_minor">for</span> {searchParams.q}.</>;

  return (
    <div className="mn_productSearchResults" ref={sectionRef}>
      <div className="mn_contentWrap">
        <SectionTitle title={sectionTitle} />
        <ShowOnMQ mobile>
          <Sticky offerTableRef={sectionRef}>
            <StickyContent className={`mn_stuck ${isUserIdentified ? '' : 'mn_isAnonymous'}`}>
              <SlideMenu customButtonClassName={hasIdsInAnyFilters ? 'mn_applied' : ''}>
                <MenuMobile filterHook={filterHook} productResultsHook={productResultsHook} />
              </SlideMenu>
            </StickyContent>
          </Sticky>
        </ShowOnMQ>
        <ShowOnMQ tablet desktop>
          <MenuDesktop
            filterHook={filterHook}
            productResultsHook={productResultsHook}
            sortedActiveFilters={sortedActiveFilters}
          />
        </ShowOnMQ>
        {hasProducts &&
          <>
            <div className="mn_prodSearchResultWrap">
              {products.map(product => (
                <ProductItem
                  product={product}
                  key={product.key}
                  rebateOptions={rebateOptions}
                  extraLabel={extraLabel}
                />
              ))}
              <ShowOnMQ tablet desktop>
                <span className="mn_flexRowFiller" /><span className="mn_flexRowFiller" />
                <span className="mn_flexRowFiller" /><span className="mn_flexRowFiller" />
              </ShowOnMQ>
            </div>
            {shouldShowExpandButton &&
              <button onClick={fetchMoreProducts} className="mn_button mn_secondaryButton mn_seeMoreButton">
                See more
              </button>
            }
          </>
        }
      </div>
    </div>
  );
}

export default (props) =>
  <ComponentErrorBoundary><ProductSearchResults {...props} /></ComponentErrorBoundary>;
